
import {DigitizationTools} from "../ToolRegistry.js";
import {createFeature} from "../Feature.js";
import {FeatureVisual} from "../FeatureVisual.js";
import {determineArcPlane} from "../geometry/arcFromSagitta.js";
import {showGeometryWarning} from "../DialogHelpers.js";
import {captureThreePointsWithPreview, toVector3Array, toPlainPoint} from "./shapeCaptureHelpers.js";
import {openArcDialog} from "../VectorEditingMenu.js";

import {t, T} from "../i18n/index.js";
// "Bue" (direct creation tool - task DEL D), as opposed to the existing
// "Bue fra pilhøjde…" which turns an ALREADY-digitized LineString range into
// an arc. Deliberately reuses every bit of that existing arc machinery
// (arcFromSagitta.js's determineArcPlane()/continuous sweep/dynamic
// sampling, arcRangeStore.js's ArcRange definition, and
// VectorEditingMenu.js's own openArcDialog() live-preview/Rediger/Vend/
// Opløs dialog, exported for this purpose) - task: "Der må ikke opstå en
// anden arc-matematik."
//
// ── Why 3 clicks, not 2 ────────────────────────────────────────────────
// Two endpoints alone fix a chord, not a PLANE - infinitely many circles
// pass through any two points, one for every possible rotation about the
// chord axis (task: "To endpoints alene definerer ikke et 3D-bueplan").
// This tool's stable resolution: start, slut, and a THIRD point that
// signals which side/plane the arc should bulge toward - fed directly into
// the EXISTING determineArcPlane(chordStart, chordEnd, interiorPoints)
// (arcFromSagitta.js) as its one-element `interiorPoints` array, exactly
// the same "average perpendicular bulge of the known interior geometry"
// signal that function already uses for an ordinary "Bue fra pilhøjde" on a
// real range with real interior vertices - no new plane-resolution
// algorithm, just a new (and, for a single point, still fully deterministic
// and camera-independent) source for that same one signal.
export const ArcTool = {
	id: "digitize_arc",
	name: T("Bue"),
	category: "basic",
	kind: "registration",
	toolbarGroup: "registration",
	toolbarOrder: 60,
	icon: "digitize_arc.svg",
	resultGeometryType: "LineString",
	tooltip: T("Digitalisér en bue direkte: start, slut og et tredje punkt til bueplan/side."),
	instructions: {
		1: T("Punkt 1/3: Venstreklik for buens startpunkt."),
		2: T("Punkt 2/3: Venstreklik for buens slutpunkt."),
		3: T("Punkt 3/3: Venstreklik for et punkt der viser buens plan og side."),
	},
	customCapture(dm, tool){ runArcCapture(dm, tool); },
};

DigitizationTools.register(ArcTool);

async function runArcCapture(dm, tool){
	const points = await captureThreePointsWithPreview(dm, tool, (n) => tool.instructions[n]);
	if(!points){ dm.stopInsertion(); return; }

	const [start, end, planePoint] = points.map(toPlainPoint);
	const plane = determineArcPlane(start, end, [planePoint]);
	if(!plane){
		showGeometryWarning(dm, t("Bue"), t("Start- og slutpunktet er sammenfaldende - der kan ikke beregnes en bue."));
		dm.stopInsertion();
		return;
	}

	const visual = new FeatureVisual("LineString");
	visual.setInfo(tool.name, "");
	dm.scene.add(visual);
	visual.setPoints(toVector3Array([start, end]));

	const feature = createFeature({
		geometryType: "LineString",
		coordinates: visual.toCoordinates(),
		category: tool.category,
		tool: tool.id,
		featureType: tool.name,
		name: `${tool.name} ${dm.featureManager.features.length + 1}`,
	});
	visual.setInfo(feature.properties.name, feature.properties.description);
	dm.registerNewFeature(feature, visual);

	// openArcDialog() itself asserts feature.domainData.arc on every preview
	// step (see its own doc comment) and, on Annuller/ESC, restores the
	// straight 2-point chord this feature started as - if the user cancels
	// out entirely, that straight leftover chord is removed below (task:
	// "Cancel efterlader ingen permanent feature"), same convention
	// circleTool.js/rectangleTool.js already use.
	const committed = await openArcDialog(dm, visual, {startIndex: 0, endIndex: 1, initialSagittaCm: 0, initialPlane: plane});
	if(!committed) dm.removeFeature(feature.id);

	dm.stopInsertion();
}
